import { component } from 'picoapp';
import Swiper, { Navigation, Pagination } from 'swiper';
import { fixRoot, unfixRoot } from '../../../js/utils/fix-root';
import Modal from '../../../js/utils/modal';
import initProductCardMediaHover from '../../../js/utils/product-card-media-hover';
import hydrateProductCardDeferredMedia from '../../../js/utils/hydrate-product-card-deferred-media';

export default component(async (node) => {
  /**
   * Data + elements
   */
  const mediaItems = node.querySelectorAll('.js-mediaItem');
  const mediaSliderElement = node.querySelector('.js-mediaSlider');
  const allowTouchMove = !mediaSliderElement.closest('.swiper .swiper'); // If the slider is inside another swiper, disable touch move
  const mediaSliderPrevBtn = node.querySelector('.js-mediaSliderPrevBtn');
  const mediaSliderNextBtn = node.querySelector('.js-mediaSliderNextBtn');
  const mediaSliderCount = node.querySelector('.js-mediaSliderCount');
  const mediaSliderPagination = node.querySelector('.js-mediaSliderPagination');
  const lightboxElement = node.querySelector('.js-lightbox');
  const lightboxSliderElement = node.querySelector('.js-lightboxSlider');
  const lightboxSliderCount = node.querySelector('.js-lightboxSliderCount');
  const lightboxSliderPrevBtn = node.querySelector('.js-lightboxSliderPrevBtn');
  const lightboxSliderNextBtn = node.querySelector('.js-lightboxSliderNextBtn');
  const lightboxMediaItems = node.querySelectorAll('.js-lightboxMediaItem');
  const lightboxDesktopNavItems = node.querySelectorAll('.js-lightboxDesktopNavItem');
  const lightboxCloseBtns = node.querySelectorAll('.js-lightboxCloseBtn');

  /**
   * Functions
   */
  const openLightbox = (lightbox, slider, index = 0) => {
    lightbox.open();
    slider.slideTo(index);
    fixRoot();
  };

  const closeLightbox = (lightbox) => {
    lightbox.close();
    unfixRoot();
  };

  const createMediaSlider = () => (() => new Swiper(mediaSliderElement, {
    modules: [Navigation, Pagination],
    slidesPerView: 'auto',
    allowTouchMove: allowTouchMove,
    navigation: {
      prevEl: mediaSliderPrevBtn,
      nextEl: mediaSliderNextBtn,
    },
    pagination: {
      el: mediaSliderPagination,
      type: 'bullets',
      bulletClass: 'product__media-slider__page-bullet',
      bulletActiveClass: 'product__media-slider__page-bullet--active',
    },
    on: {
      slideChange: (data) => {
        if (mediaSliderCount) mediaSliderCount.textContent = data.activeIndex + 1;
      },
    },
  }))();

  const createLightboxSlider = () => new Swiper(lightboxSliderElement, {
    modules: [Navigation],
    slidesPerView: 'auto',
    navigation: {
      prevEl: lightboxSliderPrevBtn,
      nextEl: lightboxSliderNextBtn,
    },
    on: {
      slideChange: (data) => {
        lightboxSliderCount.textContent = data.activeIndex + 1;

        lightboxDesktopNavItems.forEach((item) => {
          if (parseInt(item.dataset.index, 10) === data.activeIndex) {
            item.classList.add('product__lightbox__nav-item--active');
          } else {
            item.classList.remove('product__lightbox__nav-item--active');
          }
        });
      },
    },
  });

  const initLightboxZoom = () => {
    lightboxMediaItems.forEach((item) => {
      const mediaElement = item.querySelector('img,video');
      const cursor = item.querySelector('.js-lightboxCursor');

      mediaElement.addEventListener('mousemove', (event) => {
        const { top } = mediaElement.getBoundingClientRect();

        cursor.style.top = `${event.clientY - (Math.max(top, 0))}px`;
        cursor.style.left = `${event.clientX}px`;
      });

      mediaElement.addEventListener('mouseenter', () => {
        cursor.hidden = false;
      });

      mediaElement.addEventListener('mouseout', () => {
        cursor.hidden = true;
      });

      mediaElement.addEventListener('click', () => {
        item.classList.toggle('product__lightbox__media-item--zoomed');
      });
    });
  };

  const loadDeferredMedia = (full = true) => {
    hydrateProductCardDeferredMedia(node, { full });
  };

  const hydrateTemplateSlides = () => {
    const swiperWrapper = mediaSliderElement.querySelector('.swiper-wrapper');
    const templates = node.querySelectorAll('template.js-deferredSlide');

    if (templates.length === 0) return;

    templates.forEach((template) => {
      const clone = template.content.cloneNode(true);
      const img = clone.querySelector('.js-deferredImage');
      const video = clone.querySelector('.js-deferredVideo');

      if (img) {
        img.srcset = img.dataset.deferredSrcset;
        img.src = img.dataset.deferredSrc;
        if (img.complete) {
          img.classList.remove('opacity-0');
        } else {
          img.onload = () => img.classList.remove('opacity-0');
        }
      }

      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.src = video.dataset.src;
        video.load();
      }

      swiperWrapper.appendChild(clone);
      template.remove();
    });
  };

  const init = () => {
    const productItem = node.closest('.js-productItem');

    if (productItem) {
      const onFirstHover = () => {
        loadDeferredMedia(true);
        productItem.removeEventListener('mouseenter', onFirstHover);
      };
      productItem.addEventListener('mouseenter', onFirstHover);
    } else {
      // PDP context: clone deferred template slides into the DOM before Swiper
      // initialises so all slides are available immediately on swipe.
      hydrateTemplateSlides();
    }

    const mediaSlider = createMediaSlider();

    if (productItem) {
      mediaSlider.on('beforeTransitionStart', () => {
        loadDeferredMedia(true);
        mediaSlider.update();
      });
    }

    if (productItem?.querySelector('.js-productCardMediaStack')) {
      initProductCardMediaHover(productItem);
    }

    const lightbox = Modal(lightboxElement);
    const lightboxSlider = createLightboxSlider();

    mediaItems.forEach((item) => {
      const index = parseInt(item.dataset.index, 10);

      item.addEventListener('click', () => {
        openLightbox(lightbox, lightboxSlider, index);
      });
    });

    lightboxDesktopNavItems.forEach((item) => {
      const { index } = item.dataset;

      item.addEventListener('click', () => {
        lightboxSlider.slideTo(index);
      });
    });

    lightboxCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => closeLightbox(lightbox));
    });

    initLightboxZoom();
  };

  /**
   * Init
   */
  init();
});
